import { LabReport } from "@/types/LabReport"
import { useQueryClient } from "@tanstack/react-query"
import { FC, useEffect, useState } from "react"
import { useApp } from "../../AppProvider"
import { useAssignPatient } from "../../hooks/useLabReports"
import { useGetPatients } from "../../hooks/usePatients"
import { Patient } from "../../types/Patient"
import { DebouncedInput } from "../common/DebouncedInput"
import { ErrorMessage } from "../common/Feedback/ErrorMessage"
import { LoadingMessage } from "../common/Feedback/LoadingMessage"
import Modal from "../common/Modal"

interface PatientSelectionModalProps {
  open: boolean
  setOpen: (open: boolean) => void
  labReport?: LabReport
  organizationId?: string
}

export const PatientSelectionModal: FC<PatientSelectionModalProps> = ({
  open,
  setOpen,
  labReport,
  organizationId,
}) => {
  const { token, addToast, orgId } = useApp()
  const [search, setSearch] = useState("")
  const [filteredPatients, setFilteredPatients] = useState<Patient[]>([])
  const queryClient = useQueryClient()

  const { data: patients, isLoading, error } = useGetPatients(orgId, token)
  const assignPatientMutation = useAssignPatient(orgId, token)

  useEffect(() => {
    if (!patients) {
      setFilteredPatients([])
      return
    }

    let filtered = patients

    // If we have a clientId, prioritize patients with matching clientId
    if (labReport?.clientId) {
      const exactMatch = patients.filter((p) => p.clientId === labReport?.clientId)
      const partialMatches = patients.filter(
        (p) =>
          p.clientId !== labReport?.clientId &&
          (p.clientId?.toLowerCase().includes(labReport?.clientId.toLowerCase()) ||
            p.name.toLowerCase().includes(labReport?.clientId.toLowerCase())),
      )
      filtered = [...exactMatch, ...partialMatches]
    }

    // Apply search filter
    if (search) {
      filtered = filtered.filter(
        (patient) =>
          patient.name.toLowerCase().includes(search.toLowerCase()) ||
          patient.owner?.toLowerCase().includes(search.toLowerCase()) ||
          patient.clientId?.toLowerCase().includes(search.toLowerCase()),
      )
    }

    setFilteredPatients(filtered)
  }, [patients, search, labReport?.clientId])

  const handleSelectPatient = async (patient: Patient) => {
    if (!labReport?.id) {
      addToast("Error", "No lab report ID available", "error")
      return
    }

    try {
      await assignPatientMutation.mutateAsync({
        labId: labReport.id,
        patientId: patient._id,
      })
      addToast("Success", `Patient ${patient.name} has been assigned to this lab report`, "success")
      setOpen(false)
    } catch (error) {
      addToast("Error", `Failed to assign patient: ${(error as Error).message}`, "error")
    }
  }

  return (
    <Modal open={open} setOpen={(val) => setOpen(val as boolean)}>
      <div className="w-lg">
        <h3 className="mb-4 text-lg font-semibold text-gray-900">Select Patient</h3>

        {labReport?.clientId && (
          <div className="mb-4 rounded-md bg-blue-50 p-3">
            <p className="text-sm text-blue-700">
              Searching for patients with Client ID: <strong>{labReport?.clientId}</strong>
            </p>
          </div>
        )}

        <div className="mb-4">
          <DebouncedInput
            type="search"
            className="input w-full"
            placeholder="Search by patient name, owner, or client ID..."
            value={search}
            onChange={setSearch}
          />
        </div>

        <div className="max-h-96 overflow-y-auto">
          {isLoading ? (
            <LoadingMessage />
          ) : error ? (
            <ErrorMessage message={error.message} />
          ) : filteredPatients.length === 0 ? (
            <div className="py-8 text-center text-gray-500">
              {search || labReport?.clientId
                ? "No patients found matching your criteria"
                : "No patients found"}
            </div>
          ) : (
            <div className="space-y-2">
              {filteredPatients.map((patient) => (
                <button
                  key={patient._id}
                  onClick={() => handleSelectPatient(patient)}
                  disabled={assignPatientMutation.isPending}
                  className="hover:border-primary-300 hover:bg-primary-50 w-full rounded-md border border-gray-200 p-3 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-medium text-gray-900">{patient.name}</div>
                      {patient.owner && <div className="text-sm text-gray-600">Owner: {patient.owner}</div>}
                      {patient.clientId && (
                        <div className="text-sm text-gray-500">Client ID: {patient.clientId}</div>
                      )}
                    </div>
                    {labReport?.clientId === patient.clientId && (
                      <div className="rounded bg-green-100 px-2 py-1 text-xs text-green-700">Exact match</div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-x-2">
          {assignPatientMutation.isPending && (
            <div className="flex items-center text-sm text-gray-600">
              <div className="border-t-primary-600 mr-2 h-4 w-4 animate-spin rounded-full border-2 border-gray-300"></div>
              Assigning patient...
            </div>
          )}
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setOpen(false)}
            disabled={assignPatientMutation.isPending}>
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  )
}
